// Copyright (c) 2024, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  useTheme,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { RootState } from "../store/rootReducer";
import { Connector } from "../types/types";
import { useGetConnectorLogoQuery } from "../apis/connectorApis";
import { useSelector } from "react-redux";
import {
  generateRandomIconColor,
  imageFromArray,
} from "../utils/utils";
import { Link } from "react-router-dom";

const ConnectorCard: React.FC<{ connector: Connector }> = ({ connector }) => {
  const theme = useTheme();
  const [image, setImage] = useState<string | null>(null);
  const [randomColor, setRandomColor] = useState<string>(
    theme.palette.background.paper
  );

  const isDarkTheme = useSelector((state: RootState) => state.theme);
  const { data: logoResponse, isLoading } = useGetConnectorLogoQuery(
    connector.repoName
  );

  useEffect(() => {
    setImage(imageFromArray(logoResponse?.blobContent));
    if (!isLoading) {
      setRandomColor(generateRandomIconColor(connector.connectorName));
    }
  }, [logoResponse, isLoading]);

  return (
    <Grid item lg={3} xs={12} sm={6} md={4} key={connector.repoName}>
      <Link
        style={{ textDecoration: "none" }}
        to={`/connector/${connector.repoName}`}
      >
        <Card
          sx={{
            p: 2,
            pb: 0,
            height: 195,
            borderRadius: "8px",
            cursor: "pointer",
            backgroundImage: "none",
          }}
        >
          <Tooltip title={connector.connectorName}>
            <Typography
              sx={{
                pb: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontSize: "1.125rem",
              }}
            >
              <strong>{connector.connectorName}</strong>
            </Typography>
          </Tooltip>

          {!isLoading && image ? (
            <div
              style={{
                width: "95px",
                height: "90px",
                overflow: "hidden",
                borderRadius: "4px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {!logoResponse ? (
                <Typography
                  sx={{
                    backgroundColor: randomColor,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2.3rem",
                    fontWeight: "bold",
                    color: isDarkTheme
                      ? theme.palette.secondary.main
                      : theme.palette.primary.dark,
                  }}
                >
                  {connector.connectorName
                    ? connector.connectorName
                        .split(" ")
                        .map((word) => word.replace(/[^a-zA-Z]/g, ""))
                        .slice(0, 2)
                        .map((word) => word.charAt(0).toUpperCase())
                        .join("")
                    : ""}
                </Typography>
              ) : (
                <img
                  src={image}
                  alt={`${connector.connectorName} logo`}
                  style={{
                    width: "auto",
                    height: "100%",
                    borderRadius: "4px",
                  }}
                />
              )}
            </div>
          ) : (
            <div style={{ height: "90px" }}>
              <Skeleton variant="rectangular" width={95} height={90} />
            </div>
          )}

          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
            {[
              { label: "Category", value: connector.category },
              { label: "Owner", value: connector.owner },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75, overflow: "hidden" }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    flexShrink: 0,
                    fontSize: "0.75rem",
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.disabled, flexShrink: 0, fontSize: "0.65rem" }}
                >
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {value || "N/A"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Card>
      </Link>
    </Grid>
  );
};

export default ConnectorCard;
